import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { format, subMonths, addMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { getDefaultPrizeForMonth, DEFAULT_RANKING_PRIZES, DEFAULT_VOLUME_BONUSES } from '@/lib/defaultPrizes';

export interface Prize {
  id: string;
  month_year: string;
  prize_name: string;
  prize_description: string | null;
  prize_type?: 'ranking_winner' | 'volume_bonus' | 'achievement_badge' | 'special_event' | 'milestone';
  prize_category?: string | null;
  prize_value?: number | null;
  winner_id: string | null;
  winner_name?: string;
  winner_ubicacion?: string | null;
  claimed_at: string | null;
  claimed_by: string | null;
  status: 'pending' | 'claimable' | 'claimed';
  claim_code: string | null;
  created_at: string;
  isDefault?: boolean; // Indicates if this is a virtual default prize
}

export interface RankingEntry {
  seller_id: string;
  nombre: string;
  ubicacion_tienda: string | null;
  total_ventas: number;
  total_canjes: number;
  total_ahorros: number;
  puntuacion: number;
  // New contest fields
  bronce_sales: number;
  plata_sales: number;
  oro_sales: number;
  membership_sale_points: number;
  redemption_points: number;
  passive_redemption_points: number;
  savings_points: number;
  cycle_milestone_points: number;
  total_score: number;
}

export function usePrizes(userId?: string) {
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [currentPrize, setCurrentPrize] = useState<Prize | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentRanking, setCurrentRanking] = useState<RankingEntry[]>([]);
  const [userPosition, setUserPosition] = useState<number | null>(null);

  const getCurrentMonthYear = () => format(new Date(), 'yyyy-MM');
  const getPreviousMonthYear = () => format(subMonths(new Date(), 1), 'yyyy-MM');

  // Generate list of months (past 6 + current + future 11)
  const generateMonthList = useCallback(() => {
    const months: string[] = [];
    const now = new Date();
    
    // Past 6 months
    for (let i = 6; i > 0; i--) {
      months.push(format(subMonths(now, i), 'yyyy-MM'));
    }
    
    // Current + next 11 months
    for (let i = 0; i < 12; i++) {
      months.push(format(addMonths(now, i), 'yyyy-MM'));
    }
    
    return months;
  }, []);

  const fetchPrizes = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all prizes from database (only custom/assigned prizes)
      const { data: prizesData, error } = await supabase
        .from('monthly_prizes')
        .select('*')
        .order('month_year', { ascending: false });

      if (error) throw error;

      // Fetch winner names for prizes with winners
      const dbPrizesWithNames: Prize[] = [];
      for (const prize of prizesData || []) {
        let winnerName = undefined;
        let winnerUbicacion = undefined;
        
        if (prize.winner_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('nombre, ubicacion_tienda')
            .eq('user_id', prize.winner_id)
            .maybeSingle();
          
          winnerName = profile?.nombre;
          winnerUbicacion = profile?.ubicacion_tienda;
        }
        
        dbPrizesWithNames.push({
          ...prize,
          status: prize.status as 'pending' | 'claimable' | 'claimed',
          prize_type: prize.prize_type as Prize['prize_type'],
          prize_category: prize.prize_category,
          prize_value: prize.prize_value,
          winner_name: winnerName,
          winner_ubicacion: winnerUbicacion,
          isDefault: false,
        });
      }

      // Generate complete list of prizes (DB prizes + default virtual prizes)
      const monthsToShow = generateMonthList();
      const allPrizes: Prize[] = monthsToShow.map(monthYear => {
        // Check if there's a custom prize in DB for this month
        const dbPrize = dbPrizesWithNames.find(p => p.month_year === monthYear);
        if (dbPrize) {
          return dbPrize;
        }
        
        // Otherwise, return a virtual default prize
        const defaultPrize = getDefaultPrizeForMonth(monthYear);
        return {
          id: `default-${monthYear}`, // Virtual ID
          ...defaultPrize,
          winner_id: null,
          winner_name: undefined,
          winner_ubicacion: undefined,
          claimed_at: null,
          claimed_by: null,
          claim_code: null,
          created_at: new Date().toISOString(),
        } as Prize;
      });

      // Sort by month descending
      allPrizes.sort((a, b) => b.month_year.localeCompare(a.month_year));
      
      setPrizes(allPrizes);

      // Set current month prize
      const currentMonth = getCurrentMonthYear();
      const current = allPrizes.find(p => p.month_year === currentMonth);
      setCurrentPrize(current || null);

    } catch (error) {
      console.error('Error fetching prizes:', error);
    } finally {
      setLoading(false);
    }
  }, [generateMonthList]);

  const fetchCurrentRanking = useCallback(async () => {
    try {
      // Fetch seller profiles first for names
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, nombre, ubicacion_tienda');
      
      const profileMap = new Map((profiles || []).map(p => [p.user_id, p]));
      
      // Call RPC - now returns total_score from new system
      const { data, error } = await supabase.rpc('get_seller_ranking_stats' as any);

      if (error) throw error;
      
      const ranking: RankingEntry[] = (data || []).map((entry: any) => {
        const profile = profileMap.get(entry.seller_id);
        return {
          seller_id: entry.seller_id,
          nombre: profile?.nombre || 'Vendedor',
          ubicacion_tienda: profile?.ubicacion_tienda || null,
          total_ventas: entry.total_ventas || 0,
          total_canjes: entry.total_canjes || 0,
          total_ahorros: entry.total_ahorros || 0,
          // Legacy scoring - keeping for backwards compatibility
          puntuacion: entry.total_score || 0,
          // New contest fields
          bronce_sales: entry.bronce_sales || 0,
          plata_sales: entry.plata_sales || 0,
          oro_sales: entry.oro_sales || 0,
          membership_sale_points: entry.membership_sale_points || 0,
          redemption_points: entry.redemption_points || 0,
          passive_redemption_points: entry.passive_redemption_points || 0,
          savings_points: entry.savings_points || 0,
          cycle_milestone_points: entry.cycle_milestone_points || 0,
          total_score: entry.total_score || 0,
        };
      });

      // Sort by total_score (already sorted from RPC but ensure consistency)
      ranking.sort((a, b) => b.total_score - a.total_score);
      setCurrentRanking(ranking);

      // Find user position
      if (userId) {
        const position = ranking.findIndex((r) => r.seller_id === userId);
        setUserPosition(position >= 0 ? position + 1 : null);
      }

    } catch (error) {
      console.error('Error fetching ranking:', error);
    }
  }, [userId]);

  // Fetch ranking for a specific month
  const fetchRankingForMonth = useCallback(async (targetMonth: string): Promise<RankingEntry[]> => {
    try {
      // Fetch seller profiles first for names
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, nombre, ubicacion_tienda');
      
      const profileMap = new Map((profiles || []).map(p => [p.user_id, p]));
      
      // Call RPC with target month
      const { data, error } = await supabase.rpc('get_seller_ranking_stats_for_month' as any, {
        target_month: targetMonth
      });

      if (error) throw error;
      
      const ranking: RankingEntry[] = (data || []).map((entry: any) => {
        const profile = profileMap.get(entry.seller_id);
        return {
          seller_id: entry.seller_id,
          nombre: profile?.nombre || 'Vendedor',
          ubicacion_tienda: profile?.ubicacion_tienda || null,
          total_ventas: entry.total_ventas || 0,
          total_canjes: entry.total_canjes || 0,
          total_ahorros: entry.total_ahorros || 0,
          puntuacion: entry.total_score || 0,
          bronce_sales: entry.bronce_sales || 0,
          plata_sales: entry.plata_sales || 0,
          oro_sales: entry.oro_sales || 0,
          membership_sale_points: entry.membership_sale_points || 0,
          redemption_points: entry.redemption_points || 0,
          passive_redemption_points: entry.passive_redemption_points || 0,
          savings_points: entry.savings_points || 0,
          cycle_milestone_points: entry.cycle_milestone_points || 0,
          total_score: entry.total_score || 0,
        };
      });

      // Sort by total_score
      ranking.sort((a, b) => b.total_score - a.total_score);
      return ranking;

    } catch (error) {
      console.error('Error fetching ranking for month:', error);
      return [];
    }
  }, []);

  const claimPrize = async (prizeId: string) => {
    try {
      const claimCode = `PREMIO-${format(new Date(), 'MMyy')}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
      
      const { error } = await supabase
        .from('monthly_prizes')
        .update({
          status: 'claimed',
          claimed_at: new Date().toISOString(),
          claim_code: claimCode,
        })
        .eq('id', prizeId);

      if (error) throw error;

      await fetchPrizes();
      return { success: true, claimCode };
    } catch (error) {
      console.error('Error claiming prize:', error);
      return { success: false, claimCode: null };
    }
  };

  const createOrUpdatePrize = async (monthYear: string, prizeName: string, prizeDescription: string) => {
    try {
      const existing = prizes.find(p => p.month_year === monthYear);
      
      if (existing) {
        const { error } = await supabase
          .from('monthly_prizes')
          .update({
            prize_name: prizeName,
            prize_description: prizeDescription,
          })
          .eq('id', existing.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('monthly_prizes')
          .insert({
            month_year: monthYear,
            prize_name: prizeName,
            prize_description: prizeDescription,
            status: 'pending',
          });
        
        if (error) throw error;
      }

      await fetchPrizes();
      return { success: true };
    } catch (error) {
      console.error('Error saving prize:', error);
      return { success: false };
    }
  };

  const assignWinner = async (prizeId: string, winnerId: string) => {
    try {
      const { error } = await supabase
        .from('monthly_prizes')
        .update({
          winner_id: winnerId,
          status: 'claimable',
        })
        .eq('id', prizeId);

      if (error) throw error;

      await fetchPrizes();
      return { success: true };
    } catch (error) {
      console.error('Error assigning winner:', error);
      return { success: false };
    }
  };

  const markAsDelivered = async (prizeId: string, adminId: string) => {
    try {
      const { error } = await supabase
        .from('monthly_prizes')
        .update({
          status: 'claimed',
          claimed_by: adminId,
          claimed_at: new Date().toISOString(),
        })
        .eq('id', prizeId);

      if (error) throw error;

      await fetchPrizes();
      return { success: true };
    } catch (error) {
      console.error('Error marking as delivered:', error);
      return { success: false };
    }
  };

  useEffect(() => {
    fetchPrizes();
    fetchCurrentRanking();
  }, [fetchPrizes, fetchCurrentRanking]);

  const getClaimablePrize = () => {
    if (!userId) return null;
    return prizes.find(p => p.winner_id === userId && p.status === 'claimable');
  };

  // Check if user has any claimable prize (for notification)
  const hasClaimablePrize = () => {
    if (!userId) return false;
    return prizes.some(p => p.winner_id === userId && p.status === 'claimable');
  };

  // Check if user is currently #1 in ranking
  const isCurrentLeader = () => {
    if (!userId || currentRanking.length === 0) return false;
    return currentRanking[0]?.seller_id === userId;
  };

  const formatMonthYear = (monthYear: string) => {
    const [year, month] = monthYear.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return format(date, 'MMMM yyyy', { locale: es });
  };

  // Get only custom (non-default) prizes
  const getCustomPrizes = () => prizes.filter(p => !p.isDefault);
  
  // Check if a month has a custom prize
  const hasCustomPrize = (monthYear: string) => {
    return prizes.some(p => p.month_year === monthYear && !p.isDefault);
  };

  // Get volume bonuses for the current user
  const getUserVolumeBonuses = () => {
    if (!userId) return [];
    return prizes.filter(
      p => p.prize_type === 'volume_bonus' && p.winner_id === userId
    );
  };

  // Get all claimable prizes (ranking + volume bonuses)
  const getAllClaimablePrizes = () => {
    if (!userId) return [];
    return prizes.filter(p => p.winner_id === userId && p.status === 'claimable');
  };

  // Get user's current month sales for volume bonus progress
  const getUserVolumeSales = () => {
    const userRanking = currentRanking.find(r => r.seller_id === userId);
    return {
      bronce: userRanking?.bronce_sales || 0,
      plata: userRanking?.plata_sales || 0,
      oro: userRanking?.oro_sales || 0,
    };
  };

  return {
    prizes,
    currentPrize,
    loading,
    currentRanking,
    userPosition,
    claimPrize,
    createOrUpdatePrize,
    assignWinner,
    markAsDelivered,
    getClaimablePrize,
    hasClaimablePrize,
    isCurrentLeader,
    formatMonthYear,
    getCurrentMonthYear,
    getPreviousMonthYear,
    refetch: fetchPrizes,
    getCustomPrizes,
    hasCustomPrize,
    getUserVolumeBonuses,
    getAllClaimablePrizes,
    getUserVolumeSales,
    fetchRankingForMonth,
    // Export default prize constants
    DEFAULT_RANKING_PRIZES,
    DEFAULT_VOLUME_BONUSES,
  };
}
